// SearchableSelect.js - مكون جديد للبحث والاختيار مُحسن
import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';

const SearchableSelect = ({ 
    options = [], 
    value, 
    onChange, 
    placeholder = "ابحث واختر...", 
    disabled = false,
    multiple = false,
    selectedValues = [],
    onMultiChange,
    emptyMessage = "لا توجد نتائج"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const optionRefs = useRef([]); 

    // فلترة الخيارات حسب البحث - مُحسنة
    useEffect(() => {
        if (!searchTerm) {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option => {
                const labelMatch = option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase());
                const valueMatch = option.value && option.value.toString().toLowerCase().includes(searchTerm.toLowerCase());
                const subtitleMatch = option.subtitle && option.subtitle.toLowerCase().includes(searchTerm.toLowerCase());
                return labelMatch || valueMatch || subtitleMatch;
            });
            setFilteredOptions(filtered);
        }
        // إعادة تعيين المؤشر عند تغيير النتائج
        setHighlightedIndex(-1);
    }, [searchTerm, options]);

    useEffect(() => {
        if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
            optionRefs.current[highlightedIndex].scrollIntoView({
                block: "nearest",
                inline: "nearest",
            });
        }
    }, [highlightedIndex]);

    // إغلاق القائمة عند الضغط خارجها
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // التركيز على البحث عند فتح القائمة
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    // دالة مقارنة القيم المحسنة
    const isValueEqual = (val1, val2) => {
        if (val1 === val2) return true;
        return val1?.toString() === val2?.toString();
    };

    const handleSelect = (option) => {
        if (multiple) {
            const isSelected = selectedValues.some(val => isValueEqual(val, option.value));
            let newValues;
            
            if (isSelected) {
                newValues = selectedValues.filter(val => !isValueEqual(val, option.value));
            } else {
                newValues = [...selectedValues, option.value];
            }
            
            onMultiChange && onMultiChange(newValues);
        } else {
            onChange && onChange(option.value);
            setIsOpen(false);
            setSearchTerm('');
            setHighlightedIndex(-1);
        }
    };

    // دالة العرض المحسنة
    const getDisplayText = () => {
        if (multiple) {
            if (selectedValues.length === 0) return placeholder;
            if (selectedValues.length === 1) {
                const option = options.find(opt => isValueEqual(opt.value, selectedValues[0]));
                return option ? option.label : placeholder;
            }
            return `تم اختيار ${selectedValues.length} عنصر`;
        } else {
            // التحسين هنا - البحث عن القيمة المطابقة
            const selectedOption = options.find(opt => isValueEqual(opt.value, value));
            return selectedOption ? selectedOption.label : placeholder;
        }
    };

    const removeMultipleItem = (valueToRemove, e) => {
        e.stopPropagation();
        const newValues = selectedValues.filter(val => !isValueEqual(val, valueToRemove));
        onMultiChange && onMultiChange(newValues);
    };

    // دالة للتحقق من اختيار العنصر
    const isSelected = (option) => {
        if (multiple) {
            return selectedValues.some(val => isValueEqual(val, option.value));
        } else {
            return isValueEqual(value, option.value);
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            {/* المربع الرئيسي */}
            <div
                tabIndex={0}
                className={`w-full border rounded-lg px-4 py-2.5 cursor-pointer transition-all duration-200 ${
                    disabled 
                        ? 'bg-gray-50 border-gray-200 cursor-not-allowed' 
                        : isOpen
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                onKeyDown={(e) => {
                    if (disabled) return;
                    
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setIsOpen(!isOpen);
                    } else if (/^[a-zA-Zأ-ي0-9]$/.test(e.key)) {
                        if (!isOpen) {
                            setIsOpen(true);
                            setTimeout(() => {
                                if (searchInputRef.current) {
                                    searchInputRef.current.focus();
                                    searchInputRef.current.value = e.key; 
                                    setSearchTerm(e.key);
                                }
                            }, 0);
                        } else {
                            if (searchInputRef.current) {
                                searchInputRef.current.focus();
                                setTimeout(() => {
                                    searchInputRef.current.value = searchInputRef.current.value + e.key;
                                    setSearchTerm(searchInputRef.current.value);
                                }, 0);
                            }
                        }
                    }
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        {multiple && selectedValues.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                                {selectedValues.slice(0, 2).map((val, index) => {
                                    const option = options.find(opt => isValueEqual(opt.value, val));
                                    return option ? (
                                        <span 
                                            key={`${val}-${index}`}
                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800"
                                        >
                                            {option.label}
                                            <button
                                                onClick={(e) => removeMultipleItem(val, e)}
                                                className="ml-1 hover:text-blue-900"
                                                type="button"
                                            >
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ) : null;
                                })}
                                {selectedValues.length > 2 && (
                                    <span className="text-xs text-gray-500 px-2 py-0.5">
                                        +{selectedValues.length - 2} أخرى
                                    </span>
                                )}
                            </div>
                        ) : (
                            <span className={value || (multiple && selectedValues.length > 0) ? 'text-gray-900' : 'text-gray-500'}>
                                {getDisplayText()}
                            </span>
                        )}
                    </div>
                    
                    <ChevronDown 
                        size={20} 
                        className={`text-gray-400 transition-transform duration-200 ${
                            isOpen ? 'transform rotate-180' : ''
                        }`} 
                    />
                </div>
            </div>

            {/* القائمة المنسدلة */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* مربع البحث */}
                    <div className="p-3 border-b border-gray-200 bg-gray-50">
                        <div className="relative">
                            <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="ابحث هنا..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setHighlightedIndex(0);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "ArrowDown") {
                                        e.preventDefault();
                                        setHighlightedIndex((prev) =>
                                            prev < filteredOptions.length - 1 ? prev + 1 : prev
                                        );
                                    } else if (e.key === "ArrowUp") {
                                        e.preventDefault();
                                        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                                    } else if (e.key === "Enter") {
                                        e.preventDefault();
                                        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                                            handleSelect(filteredOptions[highlightedIndex]);
                                        }
                                    } else if (e.key === "Escape") {
                                        setIsOpen(false);
                                        setSearchTerm('');
                                        setHighlightedIndex(-1);
                                    }
                                }}
                                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                dir="rtl"
                            />
                        </div>
                    </div>

                    {/* قائمة الخيارات */}
                    <div className="max-h-60 overflow-y-auto">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => {
                                const selected = isSelected(option);
                                const highlighted = index === highlightedIndex;

                                return (
                                    <div
                                        ref={(el) => (optionRefs.current[index] = el)}
                                        key={`${option.value}-${index}`}
                                        className={`px-4 py-3 cursor-pointer transition-colors
                                            ${highlighted ? 'bg-blue-200 text-blue-900' : ''}
                                            ${selected ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}
                                            hover:bg-blue-50`}
                                        onClick={() => handleSelect(option)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <span className="font-medium">{option.label}</span>
                                                {option.subtitle && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {option.subtitle}
                                                    </div>
                                                )}
                                            </div>
                                            {selected && <Check size={16} className="text-blue-600 flex-shrink-0 ml-2" />}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="px-4 py-6 text-center text-gray-500">
                                <Search size={32} className="mx-auto mb-2 text-gray-300" />
                                <p>{emptyMessage}</p>
                                {searchTerm && (
                                    <p className="text-xs mt-1">
                                        لا توجد نتائج لـ "{searchTerm}"
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* إحصائيات سريعة */}
                    {filteredOptions.length > 0 && (
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600 text-center">
                            {searchTerm ? 
                                `${filteredOptions.length} من ${options.length} نتيجة` :
                                `${options.length} خيار متاح`
                            }
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;